import { useTranslation } from 'react-i18next';
import { Button, Chip, TooltipText, useToast } from '@app/core';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import classNames from 'classnames';
import { Actions } from '../../hooks/useParticipationTableActions';
import { useCallback } from 'react';
import { Participation, SelectionPayload } from '@app/api';

interface Props {
  isOpen: boolean;
  totalCount?: number;
  selectedCount?: number;
  selectedParticipations: Participation[];
  applicableBulkActions: Actions[];
  selectionPayload: SelectionPayload;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    ...theme.fonts.label,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: theme.marginBase * 7,
    minWidth: theme.marginBase * 70,
    color: theme.colors.white,
    backgroundColor: theme.colors.inkPrimary,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
    boxShadow: theme.boxShadow.base,
  },
  counter: {
    display: 'flex',
    alignItems: 'center',
  },
  gap: {
    gap: theme.marginBase,
  },
  rightActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  actionButton: {
    display: 'flex',
  },
}));

export const TableSelectionActions = ({
  isOpen,
  totalCount,
  selectedCount,
  selectedParticipations,
  applicableBulkActions,
  selectionPayload,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();

  const onClick = useCallback(
    async (action: Actions) => {
      const bulkActionFunction = action?.bulkAction;

      if (bulkActionFunction) {
        try {
          await bulkActionFunction(selectionPayload);
        } catch (e) {
          toast.error(e);
        }
      }
    },
    [selectionPayload, toast],
  );

  const canBatch = useCallback(
    (action: Actions) => {
      const selectionMode = selectionPayload.selectionMode;
      const deselectedIds = selectionPayload.deselectedIds || [];

      let canSendIds = false;
      if (selectionMode === 'all') {
        if (
          selectedParticipations?.length + deselectedIds?.length ===
          totalCount
        ) {
          canSendIds = true;
        }
      } else {
        canSendIds = true;
      }

      return action.bulkActionWithFilter || canSendIds;
    },
    [totalCount, selectedParticipations, selectionPayload],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className={classNames(classes.counter, classes.gap)}>
        <Chip>{selectedCount}</Chip>
        <span>
          {t('participants.list.selectionFooter.selected', {
            count: selectedCount,
          })}
        </span>
      </div>
      <div className={classNames(classes.rightActions, classes.gap)}>
        {applicableBulkActions.map((action: Actions) => {
          const canBatchAction = canBatch(action);

          return (
            <Button
              key={action.key}
              color={action.color}
              contentClassName={classNames(classes.actionButton, classes.gap)}
              onClick={() => onClick(action)}
              disabled={!canBatchAction}
              small
            >
              {canBatchAction ? (
                t(action.text)
              ) : (
                <TooltipText
                  title={t('participants.list.selectionFooter.coming_soon')}
                >
                  {t(action.text)}
                </TooltipText>
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
